import { useQuery } from '@tanstack/react-query';

import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';
import { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';

const MAX_RESULTS = 15;

interface IAvailablePartner {
  id: string;
  name: string;
}

interface IRawPartner {
  id: string;
  name: string;
  archived: boolean;
  closureRequest?: { Status: string };
}

const isEligibleForAssociation = (partner: IRawPartner): boolean => {
  if (partner.archived) return false;
  if (partner.closureRequest) return false;

  return true;
};

const searchAvailablePartners = async ({
  searchTerm,
  makeRequest,
}: {
  searchTerm: string;
  makeRequest: ReturnType<typeof useImperativeRequestWrapper>[1];
}): Promise<IAvailablePartner[]> => {
  const { data, error } = await makeRequest({
    path: `/admin/partners?name=${encodeURIComponent(searchTerm)}&businessTypes=PRO&hasParent=false&masterAccountEnabled=false&pageSize=${MAX_RESULTS}`,
    method: 'get',
  });

  if (error) {
    throw new Error(error?.message ?? String(error));
  }

  const partners: IRawPartner[] = data?.items ?? [];

  return partners
    .filter(isEligibleForAssociation)
    .map((p) => ({ id: p.id, name: p.name }));
};

type QueryFnType = typeof searchAvailablePartners;

export const useSearchAvailablePartners = (
  searchTerm: string,
  config?: QueryConfig<QueryFnType>
) => {
  const [, makeRequest] = useImperativeRequestWrapper('lpVillage');

  return useQuery<ExtractFnReturnType<QueryFnType>>({
    enabled: !!searchTerm,
    ...config,
    queryKey: ['available-partners-search', searchTerm],
    queryFn: () => searchAvailablePartners({ searchTerm, makeRequest }),
  });
};

export type { IAvailablePartner };
